import { FunctionalComponent } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogManager } from '@accedo/ctv-ui-dialog';
import { Button as CtvButton } from '@accedo/ctv-ui-button';
import { noop } from '@accedo/general-utils';

export type ModalButton = {
  id: string;
  label: string;
  onSelect?: () => void;
  buttonType: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
};

export type GenericPopupDialogProps = {
  id: string;
  onClose?: () => void;
  headerText?: string;
  subHeaderText?: string;
  bodyText?: string;
  buttons?: ModalButton[];
  openFocusId?: string;
  textAlignment?: 'start' | 'center' | 'end';
  buttonsAlignment?: 'start' | 'center' | 'end';
};

const defaultButtonList: ModalButton[] = [
  {
    id: 'close-error-dialog',
    label: 'Close',
    buttonType: 'primary',
  },
];

export const GenericPopupDialog: FunctionalComponent<GenericPopupDialogProps> = ({
  id,
  onClose = noop,
  headerText,
  subHeaderText,
  bodyText,
  buttons = defaultButtonList,
  openFocusId = buttons[0]?.id,
  textAlignment = 'center',
  buttonsAlignment = 'end',
}) => {
  const onCloseCb = useCallback((): void => {
    DialogManager.close(openFocusId);
    onClose();
  }, []);

  const buttonsComponents = useMemo(() => {
    if (!buttons?.length) {
      return null;
    }

    return buttons.map(({ onSelect, size, buttonType, label, ...button }) => (
      <CtvButton
        key={button.id}
        id={button.id}
        onSelect={() => {
          onSelect?.();
          onCloseCb();
        }}
        size={size || 'large'}
        buttonType={buttonType}
      >
        {label}
      </CtvButton>
    ));
  }, [buttons, onCloseCb]);

  return (
    <Dialog id={id} onOpenFocusId={openFocusId} onCloseFallbackFocusId="content">
      <DialogHeader alignment={textAlignment} headerText={headerText} subHeaderText={subHeaderText} />
      {bodyText && <DialogBody>{bodyText}</DialogBody>}
      <DialogFooter alignment={buttonsAlignment}>{buttonsComponents}</DialogFooter>
    </Dialog>
  );
};

export default GenericPopupDialog;
